import { useMemo } from "react";
import { LABELS } from "../../constants";
import AutomationText from "./AutomationText";
import styles from "./DynamicIntentDisplay.module.scss";

/**
 * Read-only Dynamic intent block (category pill(s) + intent text table).
 * Multiple categories: space-separated in intentType (e.g. "REFERENCE POSITIONAL").
 * @param {object} props
 * @param {string} [props.intentType]
 * @param {string} [props.intentText]
 * @param {string} [props.className]
 * @param {string} [props.headingId]
 */
export default function DynamicIntentDisplay({
    intentType = "",
    intentText = "",
    className = "",
    headingId = "dynamic-intent-heading",
}) {
    const categories = useMemo(
        () => String(intentType ?? "").trim().split(/\s+/).filter(Boolean),
        [intentType]
    );

    const hasIntent = categories.length > 0 || Boolean(String(intentText ?? "").trim());
    const indexNoteId = `${headingId}-zero-based-note`;

    return (
        <section
            className={`${styles.section} ${className}`.trim()}
            aria-labelledby={headingId}
            aria-describedby={hasIntent ? indexNoteId : undefined}
        >
            <div className={styles.sectionCard}>
                <div className={styles.sectionHeader}>
                    <h3 id={headingId} className={styles.sectionHeading}>
                        {LABELS.META_SECTION_DYNAMIC_INTENT}
                    </h3>
                </div>
                {!hasIntent ? (
                    <p className={styles.empty}>{LABELS.META_EMPTY_DYNAMIC_INTENT}</p>
                ) : (
                    <>
                        <p id={indexNoteId} className={styles.indexNote} role="note">
                            {LABELS.META_DYNAMIC_INTENT_ZERO_BASED_NOTE}
                        </p>
                        <div
                            className={`${styles.table} ${styles.tableIntent}`}
                            role="table"
                            aria-label={LABELS.META_SECTION_DYNAMIC_INTENT}
                        >
                        <div className={styles.trHead} role="row">
                            <span className={styles.th} role="columnheader">
                                {LABELS.META_COL_CATEGORY}
                            </span>
                            <span className={styles.th} role="columnheader">
                                {LABELS.META_COL_INTENT}
                            </span>
                        </div>
                        <div className={styles.tr} role="row">
                            <span className={styles.td} role="cell">
                                {categories.length > 0 ? (
                                    <div className={styles.categoryPills}>
                                        {categories.map((cat) => (
                                            <span key={cat} className={styles.metaCategoryPill}>
                                                {cat.toUpperCase()}
                                            </span>
                                        ))}
                                    </div>
                                ) : (
                                    <span className={styles.metaCategoryPill}>—</span>
                                )}
                            </span>
                            <span className={styles.tdIntentText} role="cell">
                                <AutomationText text={intentText} fallback="—" />
                            </span>
                        </div>
                    </div>
                    </>
                )}
            </div>
        </section>
    );
}
